import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faCheck,
  faExclamationTriangle,
  faEquals
} from '@fortawesome/free-solid-svg-icons';
import FactureItem from './FactureItem';
import PaymentItem from './PaymentItem';
import { formatPeriode, getBalanceLabel } from '../../utils/formatters';

// Render factures list
export const renderFactures = (factures, onRefresh) => {
  if (!factures || factures.length === 0) return <div>Aucune</div>;
  
  return (
    <div>
      {factures.map((facture, index) => (
        <React.Fragment key={facture.id}>
          {index > 0 && <div style={{ margin: '4px 0' }}></div>}
          <FactureItem facture={facture} onRefresh={onRefresh} />
        </React.Fragment>
      ))}
    </div>
  );
};

// Render payments list
export const renderPayments = (payments) => {
  if (!payments || payments.length === 0) return <div>Aucun</div>;
  
  return (
    <div>
      {payments.map((payment, index) => (
        <React.Fragment key={payment.id}>
          {index > 0 && <div style={{ margin: '4px 0' }}></div>}
          <PaymentItem payment={payment} />
        </React.Fragment>
      ))}
    </div>
  );
};

// Render combined transactions (regular + v2)
export const renderCombinedRows = (data, soldeCumulatif, onRefresh) => {
  return data.periodesSorted.map(periode => {
    const p = data.periodes[periode];
    const displayPeriode = formatPeriode(periode);
    
    // Factures (regular + v2)
    const allFactures = [...(p.regularFactures || []), ...(p.v2Factures || [])];
    
    // Paiements (regular + v2)
    const allPayments = [...(p.regularPayments || []), ...(p.v2Payments || [])];
    
    // Au lieu de recalculer le solde, utilisez directement la valeur calculée
    // Les données regularFactureTotal et v2FactureTotal sont déjà ajustées pour les remises
    const regularBalance = (p.regularPaymentTotal || 0) - (p.regularFactureTotal || 0);
    const v2Balance = (p.v2PaymentTotal || 0) - (p.v2FactureTotal || 0);
    const soldePeriode = regularBalance + v2Balance;
    const soldeClass = soldePeriode < 0 ? 'negative' : soldePeriode > 0 ? 'positive' : 'neutral';
    
    const getStatusIcon = (solde) => {
      if (solde < 0) return <FontAwesomeIcon icon={faExclamationTriangle} style={{color: '#b31412', marginRight: '4px'}} />;
      if (solde > 0) return <FontAwesomeIcon icon={faCheck} style={{color: '#0f733c', marginRight: '4px'}} />;
      return <FontAwesomeIcon icon={faEquals} style={{color: '#5f6368', marginRight: '4px'}} />;
    };
    
    // Utiliser le solde cumulatif du backend qui est déjà correctement calculé
    const soldeCumulatifFormatted = soldeCumulatif[periode] || 0;
    const soldeCumulatifClass = soldeCumulatifFormatted < 0 ? 'negative' : soldeCumulatifFormatted > 0 ? 'positive' : 'neutral';
    
    return (
      <tr key={periode}>
        <td>{displayPeriode}</td>
        <td>{renderFactures(allFactures, onRefresh)}</td>
        <td>{renderPayments(allPayments)}</td>
        <td className={soldeClass}>
          <div className="balance-info">
            {getStatusIcon(soldePeriode)}
            {soldePeriode}€
            <FontAwesomeIcon icon={faInfoCircle} className="balance-info-icon" />
            <span className="balance-tooltip">
              {getBalanceLabel(soldePeriode)}
            </span>
          </div>
          <span className="balance-label">
            {soldePeriode < 0 ? 'Débit' : soldePeriode > 0 ? 'Crédit' : 'Équilibré'}
          </span>
        </td>
        <td className={soldeCumulatifClass}>
          <div className="balance-info">
            {getStatusIcon(soldeCumulatifFormatted)}
            {soldeCumulatifFormatted}€
            <FontAwesomeIcon icon={faInfoCircle} className="balance-info-icon" />
            <span className="balance-tooltip">
              {getBalanceLabel(soldeCumulatifFormatted)}
            </span>
          </div>
          <span className="balance-label">
            {soldeCumulatifFormatted < 0 ? 'Cumulé débit' : soldeCumulatifFormatted > 0 ? 'Cumulé crédit' : 'Équilibré'}
          </span>
        </td>
      </tr>
    );
  });
};

// Render standard transactions
export const renderStandardRows = (data, soldeCumulatif, onRefresh) => {
  return data.periodesSorted.map(periode => {
    const p = data.periodes[periode];
    const displayPeriode = formatPeriode(periode);
    
    // Skip if no regular data
    if (!p.regularFactures && !p.regularPayments) return null;
    
    // Calculate balance
    const solde = (p.regularPaymentTotal || 0) - (p.regularFactureTotal || 0);
    const soldeClass = solde < 0 ? 'negative' : solde > 0 ? 'positive' : 'neutral';
    
    const getStatusIcon = (solde) => {
      if (solde < 0) return <FontAwesomeIcon icon={faExclamationTriangle} style={{color: '#b31412', marginRight: '4px'}} />;
      if (solde > 0) return <FontAwesomeIcon icon={faCheck} style={{color: '#0f733c', marginRight: '4px'}} />;
      return <FontAwesomeIcon icon={faEquals} style={{color: '#5f6368', marginRight: '4px'}} />;
    };
    
    // Get cumulative balance
    const soldeCumulatifFormatted = soldeCumulatif[`${periode}_regular`] || 0;
    const soldeCumulatifClass = soldeCumulatifFormatted < 0 ? 'negative' : soldeCumulatifFormatted > 0 ? 'positive' : 'neutral';
    
    return (
      <tr key={periode} className="highlight-row">
        <td>{displayPeriode}</td>
        <td>{renderFactures(p.regularFactures, onRefresh)}</td>
        <td>{renderPayments(p.regularPayments)}</td>
        <td className={soldeClass}>
          <div className="balance-info">
            {getStatusIcon(solde)}
            {solde}€
            <FontAwesomeIcon icon={faInfoCircle} className="balance-info-icon" />
            <span className="balance-tooltip">
              {getBalanceLabel(solde)}
            </span>
          </div>
          <span className="balance-label">
            {solde < 0 ? 'Débit' : solde > 0 ? 'Crédit' : 'Équilibré'}
          </span>
        </td>
        <td className={soldeCumulatifClass}>
          <div className="balance-info">
            {getStatusIcon(soldeCumulatifFormatted)}
            {soldeCumulatifFormatted}€
            <FontAwesomeIcon icon={faInfoCircle} className="balance-info-icon" />
            <span className="balance-tooltip">
              {getBalanceLabel(soldeCumulatifFormatted)}
            </span>
          </div>
          <span className="balance-label">
            {soldeCumulatifFormatted < 0 ? 'Cumulé débit' : soldeCumulatifFormatted > 0 ? 'Cumulé crédit' : 'Équilibré'}
          </span>
        </td>
      </tr>
    );
  }).filter(Boolean); // Remove null rows
};

// Render v2 transactions
export const renderV2Rows = (data, soldeCumulatif, onRefresh) => {
  return data.periodesSorted.map(periode => {
    const p = data.periodes[periode];
    const displayPeriode = formatPeriode(periode);
    
    // Skip if no v2 data
    if (!p.v2Factures && !p.v2Payments) return null;
    
    // Calculate balance
    const solde = (p.v2PaymentTotal || 0) - (p.v2FactureTotal || 0);
    const soldeClass = solde < 0 ? 'negative' : solde > 0 ? 'positive' : 'neutral';
    
    const getStatusIcon = (solde) => {
      if (solde < 0) return <FontAwesomeIcon icon={faExclamationTriangle} style={{color: '#b31412', marginRight: '4px'}} />;
      if (solde > 0) return <FontAwesomeIcon icon={faCheck} style={{color: '#0f733c', marginRight: '4px'}} />;
      return <FontAwesomeIcon icon={faEquals} style={{color: '#5f6368', marginRight: '4px'}} />;
    };
    
    // Get cumulative balance
    const soldeCumulatifFormatted = soldeCumulatif[`${periode}_v2`] || 0;
    const soldeCumulatifClass = soldeCumulatifFormatted < 0 ? 'negative' : soldeCumulatifFormatted > 0 ? 'positive' : 'neutral';
    
    return (
      <tr key={periode} className="highlight-row v2">
        <td>{displayPeriode}</td>
        <td>{renderFactures(p.v2Factures, onRefresh)}</td>
        <td>{renderPayments(p.v2Payments)}</td>
        <td className={soldeClass}>
          <div className="balance-info">
            {getStatusIcon(solde)}
            {solde}€
            <FontAwesomeIcon icon={faInfoCircle} className="balance-info-icon" />
            <span className="balance-tooltip">
              {getBalanceLabel(solde)}
            </span>
          </div>
          <span className="balance-label">
            {solde < 0 ? 'Débit' : solde > 0 ? 'Crédit' : 'Équilibré'}
          </span>
        </td>
        <td className={soldeCumulatifClass}>
          <div className="balance-info">
            {getStatusIcon(soldeCumulatifFormatted)}
            {soldeCumulatifFormatted}€
            <FontAwesomeIcon icon={faInfoCircle} className="balance-info-icon" />
            <span className="balance-tooltip">
              {getBalanceLabel(soldeCumulatifFormatted)}
            </span>
          </div>
          <span className="balance-label">
            {soldeCumulatifFormatted < 0 ? 'Cumulé débit' : soldeCumulatifFormatted > 0 ? 'Cumulé crédit' : 'Équilibré'}
          </span>
        </td>
      </tr>
    );
  }).filter(Boolean); // Remove null rows
};
// Get the footer data based on table type
export const getFooterData = (type, data, soldeCourant, soldeCourantClass, soldeCumulatif, soldeCumulatifClass, soldeFinal, soldeFinalClass, soldeFinalV2, v2SoldeClass) => {
  // Safely extract the bail object from data with fallback to empty object
  const bail = data.bail || {};

  const finance = data.finance;

  const calculatedSolde = finance.totaux.paiements.total - finance.totaux.factures.total;
  const soldeClass = calculatedSolde < 0 ? 'negative' : calculatedSolde > 0 ? 'positive' : 'neutral';
  const soldeCalcule = finance.totaux.paiements.total - finance.totaux.factures.total;

  
  switch (type) {
    case 'combined':
      return {
        factureTotal: finance.totaux.factures.total,
        paymentTotal: finance.totaux.paiements.total,
        solde: calculatedSolde,
        soldeClass: soldeCourantClass,
        cumulativeSolde: calculatedSolde,
        cumulativeSoldeClass: soldeCumulatifClass,
        borderColor: '#4f46e5'
      };
    case 'standard':
      return {
        factureTotal: finance.totaux.factures.regular,
        paymentTotal: finance.totaux.paiements.regular,
        solde: bail.estTermine ? finance.soldes.final : finance.soldes.courant,
        soldeClass: soldeFinalClass,
        cumulativeSolde: soldeFinal,  // This duplicates the same value
        cumulativeSoldeClass: soldeFinalClass,
        title: 'TOTAL STANDARD',
        borderColor: '#4f46e5'
      };
    case 'v2':
      return {
        factureTotal: finance.totaux.factures.v2,
        paymentTotal: finance.totaux.paiements.v2,
        solde: soldeFinalV2,
        soldeClass: v2SoldeClass,
        cumulativeSolde: soldeFinalV2,
        cumulativeSoldeClass: v2SoldeClass,
        title: 'TOTAL V2',
        borderColor: '#0284c7'
      };
    default:
      return {};
  }
};
